import type { IProductVariantRepository } from '@core/domain/interfaces/repositories';
import { ProductVariantEntity } from '@core/domain/entities/ProductVariant';
import { BasePouchDBAdapter } from './BasePouchDBAdapter';
import type { BasePouchDocument } from '../types';

interface ProductVariantDocument extends BasePouchDocument {
  productId: string;
  sku: string;
  barcode?: string;
  attributes: Array<{ name: string; value: string }>;
  cost: { amount: number; currency: string };
  price: { amount: number; currency: string };
  minQuantity: { value: number; unit: string };
  maxQuantity?: { value: number; unit: string };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export class ProductVariantPouchDBAdapter
  extends BasePouchDBAdapter<ProductVariantEntity>
  implements IProductVariantRepository
{
  constructor(dbName: string) {
    super(dbName, 'product_variant');
    this.createIndices();
  }

  private async createIndices(): Promise<void> {
    await this.createIndex(['sku']);
    await this.createIndex(['barcode']);
    await this.createIndex(['isActive']);
  }

  protected toDocument(variant: ProductVariantEntity): ProductVariantDocument {
    return {
      _id: variant._id,
      type: variant.type,
      version: variant.version,
      productId: variant.productId,
      sku: variant.sku,
      barcode: variant.barcode,
      attributes: variant.attributes,
      cost: variant.cost,
      price: variant.price,
      minQuantity: variant.minQuantity,
      maxQuantity: variant.maxQuantity,
      isActive: variant.isActive,
      createdAt: variant.createdAt.toISOString(),
      updatedAt: variant.updatedAt.toISOString(),
    };
  }

  protected fromDocument(doc: ProductVariantDocument): ProductVariantEntity {
    return ProductVariantEntity.restore({
      _id: doc._id,
      type: doc.type,
      version: doc.version ?? 1,
      productId: doc.productId,
      sku: doc.sku,
      barcode: doc.barcode,
      attributes: doc.attributes,
      cost: doc.cost,
      price: doc.price,
      minQuantity: doc.minQuantity,
      maxQuantity: doc.maxQuantity,
      isActive: doc.isActive,
      createdAt: new Date(doc.createdAt),
      updatedAt: new Date(doc.updatedAt),
    });
  }

  async save(entity: ProductVariantEntity): Promise<void> {
    const doc = this.toDocument(entity);
    try {
      const existing = await this.db.get(entity._id);
      await this.db.put({ ...doc, _rev: (existing as any)._rev });
    } catch (error) {
      if ((error as any).status === 404) {
        await this.db.put(doc);
      } else {
        throw this.handleError(error as any);
      }
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const doc = await this.db.get(id);
      await this.db.remove(doc);
    } catch (error) {
      if ((error as any).name === 'not_found') {
        return;
      }
      throw error;
    }
  }

  async findByProductId(productId: string): Promise<ProductVariantEntity[]> {
    const result = await this.db.find({
      selector: { type: 'product_variant', productId },
    });
    return result.docs.map(doc => this.fromDocument(doc as ProductVariantDocument));
  }

  async findBySku(sku: string): Promise<ProductVariantEntity | null> {
    const result = await this.db.find({
      selector: { type: 'product_variant', sku },
    });
    return result.docs.length > 0 ? this.fromDocument(result.docs[0] as ProductVariantDocument) : null;
  }

  async findByBarcode(barcode: string): Promise<ProductVariantEntity | null> {
    const result = await this.db.find({
      selector: { type: 'product_variant', barcode },
    });
    return result.docs.length > 0 ? this.fromDocument(result.docs[0] as ProductVariantDocument) : null;
  }

  async findActive(): Promise<ProductVariantEntity[]> {
    const result = await this.db.find({
      selector: { type: 'product_variant', isActive: true },
    });
    return result.docs.map(doc => this.fromDocument(doc as ProductVariantDocument));
  }
}
